import React, { useRef } from "react";
import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { PlusIcon, ClockIcon } from "@heroicons/react/24/outline";

export const CalendarDayCell = ({
  day,
  idx,
  items = [],
  isSelected,
  onCellClick,
  onAddActivity,
  onItemClick,
  activityTypes = [],
  loadingActivityTypes = false,
  canSchedule = true,
  wholeCellMenu = false, // If true, the whole cell triggers the dropdown menu
  getItemStyle,
  getItemName,
  handleItemHoverEnter,
  handleItemHoverLeave,
  formatTimeLocal,
}) => {
  const menuButtonRef = useRef(null);

  const cellClasses = clsx(
    "group relative p-2 border-r border-b border-gray-100 dark:border-gray-700 last:border-r-0 flex flex-col justify-between min-h-[120px] text-left transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50",
    day.isCurrentMonth
      ? "bg-white dark:bg-gray-900"
      : "bg-gray-50/50 dark:bg-gray-800/30",
    day.isToday
      ? "bg-blue-50 dark:bg-blue-900/10 ring-2 ring-blue-500 ring-inset"
      : "",
    isSelected && "ring-2 ring-primary ring-inset",
    (canSchedule || onCellClick) && "cursor-pointer"
  );

  const menuDropdown = (
    <Transition
      as={React.Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <MenuItems
        anchor={{ to: "bottom end", gap: 4 }}
        className="dark:bg-dark-700 z-50 max-h-60 w-48 overflow-y-auto rounded-xl border border-gray-200/60 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)] ring-1 ring-black/10 focus:outline-none dark:border-gray-700/50 dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
      >
        <div className="p-1">
          {loadingActivityTypes ? (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-gray-400">
              <div className="border-primary-400 h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />
              Loading...
            </div>
          ) : activityTypes.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400">
              No types available
            </div>
          ) : (
            activityTypes.map((type) => (
              <MenuItem key={type.id ?? type.channelId}>
                {({ active }) => (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddActivity && onAddActivity(day.date, type);
                    }}
                    className={clsx(
                      "flex w-full cursor-pointer items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                      active
                        ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20"
                        : "dark:text-dark-200 text-gray-700"
                    )}
                  >
                    {type.label || type.name}
                  </button>
                )}
              </MenuItem>
            ))
          )}
        </div>
      </MenuItems>
    </Transition>
  );

  const cellContent = (
    <>
      <div className="mb-2 flex items-center justify-between">
        {/* Day Number */}
        <span
          className={clsx(
            "text-sm font-bold flex items-center justify-center rounded-full w-7 h-7",
            day.isToday && "bg-primary text-white shadow-lg",
            !day.isToday && (day.isCurrentMonth ? "text-slate-700 dark:text-dark-100" : "text-gray-400 dark:text-gray-600")
          )}
        >
          {day.date.getDate()}
        </span>

        {/* Plus add button (shows on hover) */}
        {canSchedule && (
          wholeCellMenu ? (
            <div className="rounded-full bg-blue-50 p-1 text-blue-600 opacity-0 transition-all duration-200 group-hover:opacity-100 dark:bg-blue-900/20 dark:text-blue-400">
              <PlusIcon className="h-4 w-4" />
            </div>
          ) : (
            <Menu as="div" className="relative inline-block text-left z-10">
              <MenuButton
                onClick={(e) => e.stopPropagation()}
                className="rounded-full bg-blue-50 p-1 text-blue-600 opacity-0 transition-all duration-200 group-hover:opacity-100 dark:bg-blue-900/20 dark:text-blue-400 cursor-pointer flex items-center justify-center hover:scale-105"
                title="Schedule Activity"
              >
                <PlusIcon className="h-4 w-4" />
              </MenuButton>
              {menuDropdown}
            </Menu>
          )
        )}
      </div>

      {/* List of items inside cell directly */}
      <div className="max-h-[80px] flex-1 space-y-1 overflow-y-auto w-full">
        {items.map((item, itemIdx) => {
          const style = getItemStyle ? getItemStyle(item) : {
            bg: "bg-blue-50 dark:bg-blue-900/20",
            text: "text-blue-800 dark:text-blue-200",
            border: "border-blue-200 dark:border-blue-800",
            dot: "bg-blue-500",
          };

          const name = getItemName
            ? getItemName(item)
            : (item.ActivityTitle || item.activityName || item.TaskName || item.Remarks || item.name || item.title || "Activity");
          const timeStr = item.ScheduledTime || item.scheduledTime || item.DueDate || item.dueDate || "";

          return (
            <div
              key={itemIdx}
              className={clsx(
                "group/item cursor-pointer rounded-md px-1.5 py-0.5 text-[10px] leading-tight transition-all duration-200 hover:shadow-md border flex items-center justify-between gap-1",
                style.bg,
                style.text,
                style.border
              )}
              onClick={(e) => {
                e.stopPropagation();
                onItemClick && onItemClick(item);
              }}
              onMouseEnter={(e) => handleItemHoverEnter && handleItemHoverEnter(e, item)}
              onMouseLeave={handleItemHoverLeave}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex min-w-0 items-center gap-1 flex-1">
                {style.icon ? (
                  <style.icon className={clsx("h-3 w-3 flex-shrink-0", style.iconColor || style.text)} />
                ) : (
                  <ClockIcon className="h-3 w-3 flex-shrink-0" />
                )}
                {getItemName ? (
                  // Caller provides full label (e.g. "09:00 AM - Remarks")
                  <span className="truncate font-semibold">{name}</span>
                ) : (
                  // Default: show time + dash + name separately
                  <>
                    <span className="font-bold flex-shrink-0">
                      {timeStr ? (formatTimeLocal ? formatTimeLocal(timeStr) : timeStr) : "Scheduled"}
                    </span>
                    <span className="truncate opacity-95 font-semibold">
                      - {name}
                    </span>
                  </>
                )}
              </div>
              
              {/* color status dot badge */}
              <div
                className={clsx(
                  "h-1.5 w-1.5 flex-shrink-0 rounded-full",
                  style.dot
                )}
              />
            </div>
          );
        })}
      </div>
    </>
  );

  if (wholeCellMenu && canSchedule) {
    return (
      <Menu as="div" key={idx} className="relative inline-block h-full w-full text-left">
        <MenuButton ref={menuButtonRef} className="sr-only" />
        <div 
          className={cellClasses}
          onClick={(e) => {
            e.stopPropagation();
            menuButtonRef.current?.click();
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menuButtonRef.current?.click();
          }}
        >
          {cellContent}
        </div>
        {menuDropdown}
      </Menu>
    );
  }

  return (
    <div
      key={idx}
      onClick={() => onCellClick && onCellClick(day.date)}
      className={cellClasses}
    >
      {cellContent}
    </div>
  );
};

export default CalendarDayCell;
